import { addFormItem, resetFormItem } from 'store/slices/calculatorFormSlice';
import { AppDispatch } from 'store/store';

/**
 * Runtime form-field UID the brand flow-stop writes so the CMS
 * `FlowRestrictionBlock` can react. Empty / undefined = no flow stop.
 * The CMS block owns the modal copy; the frontend just decides when to trigger it.
 */
export const BRAND_FLOW_STOP_UIDS = {
	MATCHED_BRAND: 'FLOW_STOP_UNSUPPORTED_CAR_BRAND',
} as const;

/**
 * Brands that require personal handling (no online quote). Kept in code because
 * the list changes rarely and is short. The user-facing modal text is CMS-authored.
 */
export const BRAND_FLOW_STOPS: readonly string[] = [
	'Aston Martin',
	'Bentley',
	'Bugatti',
	'Ferrari',
	'Hummer',
	'Lamborghini',
	'Maserati',
	'Rolls-Royce',
	'Pagani',
	'Koenigsegg',
	'McLaren',
	'Maybach',
	'Fisker',
];

const BRAND_FLOW_STOPS_BY_UPPER: Map<string, string> = new Map(
	BRAND_FLOW_STOPS.map((brand) => [brand.toUpperCase(), brand])
);

export type SaveFormValue = (uid: string, value: unknown) => void;

/** Case-insensitive match; returns the canonical display brand name (as authored above) or undefined. */
export const matchFlowStopBrand = (brandName: string | undefined | null): string | undefined => {
	if (!brandName) return undefined;
	return BRAND_FLOW_STOPS_BY_UPPER.get(brandName.toUpperCase());
};

/**
 * Write the matched brand into RHF (so the CMS `FlowRestrictionBlock` fires)
 * and Redux (so CMS placeholders like {{FLOW_STOP_UNSUPPORTED_CAR_BRAND}} inside
 * the modal content resolve — `PlaceholderService` reads Redux, not RHF).
 * Returns the matched brand for the caller to short-circuit downstream SPD calls.
 */
export const applyBrandFlowStopField = ({
	brandName,
	saveFormValue,
	dispatch,
	contentType,
	page,
}: {
	brandName: string | undefined | null;
	saveFormValue: SaveFormValue;
	dispatch: AppDispatch;
	contentType: string;
	page: string;
}): string | undefined => {
	const matched = matchFlowStopBrand(brandName);
	saveFormValue(BRAND_FLOW_STOP_UIDS.MATCHED_BRAND, matched);
	if (matched) {
		dispatch(
			addFormItem({
				key: BRAND_FLOW_STOP_UIDS.MATCHED_BRAND,
				item: { data: { id: BRAND_FLOW_STOP_UIDS.MATCHED_BRAND, value: matched, type: contentType }, page },
			})
		);
	} else {
		dispatch(resetFormItem({ key: BRAND_FLOW_STOP_UIDS.MATCHED_BRAND }));
	}
	return matched;
};

/** Clear the flow-stop brand field in both RHF and Redux. Call at the start of a new lookup. */
export const clearBrandFlowStopField = ({
	saveFormValue,
	dispatch,
}: {
	saveFormValue: SaveFormValue;
	dispatch: AppDispatch;
}): void => {
	saveFormValue(BRAND_FLOW_STOP_UIDS.MATCHED_BRAND, undefined);
	dispatch(resetFormItem({ key: BRAND_FLOW_STOP_UIDS.MATCHED_BRAND }));
};
